import { useNavigate } from 'react-router';
import { Button, Icon } from '@efira/ui';

import { useQuota } from '../../api/queries';
import { formatReset } from '../../dashboard/QuotaPanel';
import { useLiveKind } from '../liveState';

/**
 * Пилюля квоты в верхней полосе: остаток, полоса расхода, значок акции.
 *
 * ПОЧЕМУ В ПОЛОСЕ. Остаток дневного эфира обязан быть виден ВСЕГДА (Фаза 3,
 * блок 6), а видно всегда только то, что стоит в полосе: левая колонка
 * прокручивается, центр меняет режим. До этой зоны показатель жил в шапке
 * вкладок и вместе с ними пропал - долг этапа 1, закрывается здесь первым.
 *
 * ЧЕМ ОТЛИЧАЕТСЯ ОТ ЭТАЛОНА. У них «60m left» и полоса; момент сброса не
 * назван нигде, кроме модалки тарифа, а сброс идёт в полночь UTC - в Душанбе
 * это 05:00, посреди ночного эфира. У нас момент сброса назван в подсказке
 * прямо на пилюле, и он местный.
 */
export function QuotaPill() {
  /* Место состояния эфира (единица 22): симулятор квоту не расходует,
     и пилюля его эфиром не называет. */
  const liveKind = useLiveKind();
  const quota = useQuota();
  const navigate = useNavigate();
  const data = quota.data;

  const open = () => {
    void navigate('/app/plan?from=quota');
  };

  if (data === undefined) {
    return (
      <span className="text-foreground/80 px-2 text-xs" role="status">
        …
      </span>
    );
  }

  // Pro: лимита нет, и полоса расхода показывала бы пустоту. Кнопка остаётся -
  // через неё открывается тариф.
  if (data.dailyMinutes === null) {
    return (
      <Button
        variant="ghost"
        size="s"
        className="gap-2 whitespace-nowrap"
        data-topbar="quota"
        data-live-place="quota"
        data-live-state={liveKind}
        onClick={open}
        aria-label="Тариф Pro: дневного лимита эфира нет. Открыть тариф"
      >
        <Icon name="clock" size="sm" />
        без лимита
      </Button>
    );
  }

  const remaining = data.remainingMinutes ?? 0;
  const share = Math.max(0, Math.min(1, remaining / data.dailyMinutes));
  const tone = data.exhausted
    ? 'text-destructive'
    : data.warning !== null
      ? 'text-warning'
      : 'text-foreground/80';

  return (
    <Button
      variant="ghost"
      size="s"
      className="gap-2 whitespace-nowrap"
      data-topbar="quota"
      data-live-place="quota"
      data-live-state={liveKind}
      onClick={open}
      title={`Осталось ${remaining} из ${data.dailyMinutes} мин на сегодня. Сброс ${formatReset(data)}`}
      /* Момент сброса назван и в доступном имени: подсказка `title` не
         достаётся ни с клавиатуры, ни с тач-экрана. */
      aria-label={`Осталось ${remaining} мин из ${data.dailyMinutes}. Сброс ${formatReset(data)}. Открыть тариф`}
    >
      <Icon name="clock" size="sm" />
      {/*
        ОСТАТОК НЕ ПРЯЧЕТСЯ НИКОГДА - он в полосе именно ради этого. Прячется
        то, без чего число остаётся числом: сперва полоса расхода, потом
        «из 60». Пилюля не умела сжиматься вовсе и при узком центре выносила
        треть на 462 px при ширине 322 - вместе с соседями по полосе.
      */}
      <span className={`tabular-nums ${tone}`}>
        {remaining} мин
        <span data-topbar="quota-of" className="@max-[380px]:hidden">
          {' '}
          из {data.dailyMinutes}
        </span>
      </span>
      {/* Полоса - остаток, та же величина, что и число рядом. */}
      <span
        aria-hidden="true"
        data-topbar="quota-scale"
        /* Трек шире и виден по краям: полоса 40x4 при полном остатке читалась
           как декоративная линейка, а не как индикатор. */
        className="bg-foreground/20 relative h-1.5 w-20 shrink-0 overflow-hidden rounded-full ring-foreground/10 ring-1 @max-[600px]:hidden"
      >
        <span
          className={`absolute inset-y-0 start-0 rounded-full ${
            data.exhausted ? 'bg-destructive' : data.warning !== null ? 'bg-warning' : 'bg-primary'
          }`}
          style={{ width: `${share * 100}%` }}
        />
      </span>
    </Button>
  );
}

/**
 * Метка тарифа рядом с пилюлей квоты - отдельный элемент, как у эталона.
 *
 * Стоит рядом с остатком намеренно: это точка, где стример упирается в предел
 * бесплатного тарифа, и разговор о Pro уместен ровно здесь.
 *
 * У эталона здесь значок скидки. У нас скидки нет - как нет ни цены, ни
 * оплаты, - и рисовать «−50 %» значило бы обещать несуществующее. Место
 * остаётся, содержимое говорит правду.
 */
export function QuotaPromo() {
  const navigate = useNavigate();
  const quota = useQuota();

  if (quota.data?.dailyMinutes === null) {
    return null;
  }

  return (
    <Button
      /*
       * ЗАПОЛНЕННАЯ КНОПКА, А НЕ ПРИЗРАЧНАЯ. Находка владельца по парам
       * (единица 15): «Pro» в полосе читается подписью, и никто не
       * догадывается, что по нему нажимают. У призрачного варианта
       * пресета нет ни фона, ни рамки, а значка рядом с текстом здесь
       * тоже нет - от кнопки остаётся одно слово. Сосед по полосе,
       * пилюля квоты, призрачной остаётся законно: у неё есть значок.
       *
       * Вариант `secondary` - собственная заливка пресета: видно, что
       * это орган управления, и видно нажатие (наведение, `active`).
       * Своего цвета не добавлено ни одного.
       */
      variant="secondary"
      size="s"
      className="ms-1 hidden h-6 whitespace-nowrap @[640px]:inline-flex"
      data-topbar="quota-promo"
      onClick={() => {
        void navigate('/app/plan?from=quota');
      }}
      aria-label="Тариф Pro: открыть, что в него входит и сколько он стоит"
      /* Высота 24 px и зазор от пилюли: цель меньше 24x24 вплотную к соседней
         нарушает WCAG 2.2, 2.5.8. На узкой ТРЕТИ метка прячется - там за неё
         отвечает пункт «Тариф Pro» в меню аккаунта. Прячется по ширине трети,
         а не окна: границы колонок тянутся мышью, и широкое окно ничего не
         обещает узкому центру. */
      /* Порог 600 - третья ступень лестницы полосы (`TopBar.tsx`). Прежний 560, а не 520: на окне 1200 центральная треть - 531 px, и
         промо не помещалось ровно на 12 пикселей. Поймано воротами строк
         управления после того, как в них добавили узкие окна. */
    >
      {/*
        «Pro скоро» обещало срок, которого нет. Замена на «Что даёт Pro»
        оказалась на 11 px длиннее и вынесла центральную треть полосы за
        её край - поймано воротами строк управления в тот же прогон.
        Осталось одно слово: смысл несёт `aria-label`, а места в полосе
        столько, сколько есть.
      */}
      Pro
    </Button>
  );
}
